import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal } from '@material-ui/core';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { useDispatch } from 'react-redux';

import { isMapCard, updateCard } from '../redux/cardDbSlice';
import useOneCardDb from '../hooks/useOneCardDb';
import { initial, getCardComponent } from '../card_components';
import ResizeCardPad from './ResizeCardPad';

type ModalContentProps = {
  x: number;
  y: number;
}
const ModalContent = styled.div<ModalContentProps>`
  position: absolute;
  top: ${props => props.y + 'px'};
  left: ${props => props.x + 'px'};
`
type ScaleWrapperProps = {
  scale: number;
}
const ScaleWrapper = styled.div<ScaleWrapperProps>`
  transform-origin: left top;
  transform: ${props => 'scale(' + props.scale + ')'};
`

type ResizeBoxSvgProps = {
  x: number;
  y: number;
}
const ResizeBoxSvg = styled.svg<ResizeBoxSvgProps>`
  position: absolute;
  z-index: 2;
  pointer-events: none;
  overflow: visible;
`
type ResizeHandleProps = {
  translateValue: {x: number, y: number};
}
const ResizeHandleRect = styled.rect<ResizeHandleProps>`
  position: absolute;
  transform: ${props => 'translate(' + props.translateValue.x + 'px, ' + props.translateValue.y + 'px) !important'};
  pointer-events: painted;
`
const ResizeHandleG = styled.g`
  position: absolute;
  transform: translate(0, 0);
`

type ResizeDataType = { xFrom: number; xTo: number; yFrom: number; yTo: number }

type ResizeBoxProps = {
  resizeData: ResizeDataType;
  onResize: (data: ResizeDataType) => void;
  scaleValue: number;
  minWidth: number;
  minHeight: number;
}
const ResizeBox: React.FC<ResizeBoxProps> = ({ resizeData, onResize, scaleValue, minWidth, minHeight }) => {
  const xFirst = resizeData.xFrom;
  const xTo = resizeData.xTo;
  const yFirst = resizeData.yFrom;
  const yTo = resizeData.yTo;
  const width = xTo - xFirst;
  const height = yTo - yFirst;
  const cornerWidth = width > height ? height / 4 * scaleValue : width / 4 * scaleValue;
  const color = '#0d47a1';
  type SpotType = 'top' | 'rightTop' | 'right' | 'rightBottom' | 'bottom' | 'leftBottom' | 'left' | 'leftTop'
  const [dragging, setDragging] = useState<boolean>(false);

  const dragStart = (cursor: string) => {
    document.body.style.cursor = cursor;
    setDragging(true);
  }
  const dragEnd = () => {
    document.body.style.cursor = 'auto';
    setDragging(false);
  }
  const sin = (rotation: number) => {
    return rotation % 180 === 0 ? 0 : Math.sin(Math.PI * rotation / 180);
  }
  const cos = (rotation: number) => {
    return (rotation - 90) % 180 === 0 ? 0 : Math.cos(Math.PI * rotation / 180);
  }
  const getDragVerticalAngle = (x: number, y: number): number => {
    if (y === 0 && x === 0) return 0;
    if (y === 0 && x > 0) return 90;
    else if (y === 0 && x < 0) return 270;
    else if (x >= 0 && y > 0) return Math.atan(Math.abs(x) / Math.abs(y)) * 180 / Math.PI;
    else if (x >= 0 && y < 0) return 180 - Math.atan(Math.abs(x) / Math.abs(y)) * 180 / Math.PI;
    else if (x < 0 && y < 0) return Math.atan(Math.abs(x) / Math.abs(y)) * 180 / Math.PI + 180;
    else return 360 - Math.atan(Math.abs(x) / Math.abs(y)) * 180 / Math.PI;
  }
  const getDragHorizontalAngle = (x: number, y: number): number => {
    if (y === 0 && x === 0) return 0;
    if (x === 0 && y > 0) return 90;
    else if (x === 0 && y < 0) return 270;
    else if (y >= 0 && x > 0) return Math.atan(Math.abs(y) / Math.abs(x)) * 180 / Math.PI;
    else if (y >= 0 && x < 0) return 180 - Math.atan(Math.abs(y) / Math.abs(x)) * 180 / Math.PI;
    else if (y < 0 && x < 0) return Math.atan(Math.abs(y) / Math.abs(x)) * 180 / Math.PI + 180;
    else return 360 - Math.atan(Math.abs(y) / Math.abs(x)) * 180 / Math.PI;
  }
  const getHandlePosition = (spot: SpotType): {x: number, y: number} => {
    let handleXPosition = 0;
    let handleYPosition = 0;
    if (spot === 'top') {
      handleXPosition = 0;
      handleYPosition = -height / 2;
    } else if (spot === 'rightTop') {
      handleXPosition = width / 2;
      handleYPosition = -height / 2;
    } else if (spot === 'right') {
      handleXPosition = width / 2;
      handleYPosition = 0;
    } else if (spot === 'rightBottom') {
      handleXPosition = width / 2;
      handleYPosition = height / 2;
    } else if (spot === 'bottom') {
      handleXPosition = 0;
      handleYPosition = height / 2;
    } else if (spot === 'leftBottom') {
      handleXPosition = -width / 2;
      handleYPosition = height / 2;
    } else if (spot === 'left') {
      handleXPosition = -width / 2;
      handleYPosition = 0;
    } else if (spot === 'leftTop') {
      handleXPosition = -width / 2;
      handleYPosition = -height / 2;
    }
    const handleX = xFirst + width / 2 + handleXPosition;
    const handleY = yFirst + height / 2 + handleYPosition;
    return { x: handleX, y: handleY };
  }
  const resizeItemTop = (data: DraggableData) => {
    const origin = getHandlePosition('top');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const dragAngle = getDragVerticalAngle(-drag.x, drag.y);
    const dragValue = cos(dragAngle) === 0 ? Math.abs(drag.x / sin(dragAngle)) : Math.abs(drag.y / cos(dragAngle));
    const resize = dragValue * cos(-dragAngle);

    const resizedHeight = height - resize;
    const newY = yFirst + resize;
    const newData = {
      xFrom: xFirst,
      xTo: xTo,
      yFrom: resizedHeight < minHeight ? yFirst : newY < 0 ? 0 : newY,
      yTo: yTo
    };
    onResize(newData);
  }
  const resizeItemRightTop = (data: DraggableData) => {
    const origin = getHandlePosition('rightTop');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const resize = { width: drag.x, height: drag.y }

    const resizedWidth = width + resize.width;
    const resizedHeight = height - resize.height;
    const newY = yFirst + resize.height;
    const newData = {
      xFrom: xFirst,
      xTo: resizedWidth < minWidth ? xTo : xTo + resize.width,
      yFrom: resizedHeight < minHeight ? yFirst : newY < 0 ? 0 : newY,
      yTo: yTo
    };
    onResize(newData);
  }
  const resizeItemRight = (data: DraggableData) => {
    const origin = getHandlePosition('right');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const dragAngle = getDragHorizontalAngle(drag.x, drag.y);
    const dragValue = sin(dragAngle) === 0 ? Math.abs(drag.x / cos(dragAngle)) : Math.abs(drag.y / sin(dragAngle));
    const resize = dragValue * cos(-dragAngle);

    const resizedWidth = width + resize;
    const newData = {
      xFrom: xFirst,
      xTo: resizedWidth < minWidth ? xTo : xTo + resize,
      yFrom: yFirst,
      yTo: yTo
    };
    onResize(newData);
  }
  const resizeItemRightBottom = (data: DraggableData) => {
    const origin = getHandlePosition('rightBottom');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const resize = { width: drag.x, height: drag.y }

    const resizedWidth = width + resize.width;
    const resizedHeight = height + resize.height;
    const newData = {
      xFrom: xFirst,
      xTo: resizedWidth < minWidth ? xTo : xTo + resize.width,
      yFrom: yFirst,
      yTo: resizedHeight < minHeight ? yTo : yTo + resize.height
    };
    onResize(newData);
  }
  const resizeItemBottom = (data: DraggableData) => {
    const origin = getHandlePosition('bottom');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const dragAngle = getDragVerticalAngle(-drag.x, drag.y);
    const dragValue = cos(dragAngle) === 0 ? Math.abs(drag.x / sin(dragAngle)) : Math.abs(drag.y / cos(dragAngle));
    const resize = dragValue * cos(-dragAngle);

    const resizedHeight = height + resize;
    const newData = {
      xFrom: xFirst,
      xTo: xTo,
      yFrom: yFirst,
      yTo: resizedHeight < minHeight ? yTo : yTo + resize
    };
    onResize(newData);
  }
  const resizeItemLeftBottom = (data: DraggableData) => {
    const origin = getHandlePosition('leftBottom');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const resize = { width: drag.x, height: drag.y }

    const resizedWidth = width - resize.width;
    const newX = xFirst + resize.width;
    const resizedHeight = height + resize.height;
    const newData = {
      xFrom: resizedWidth < minWidth ? xFirst : newX < 0 ? 0 : newX,
      xTo: xTo,
      yFrom: yFirst,
      yTo: resizedHeight < minHeight ? yTo : yTo + resize.height
    };
    onResize(newData);
  }
  const resizeItemLeft = (data: DraggableData) => {
    const origin = getHandlePosition('left');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const dragAngle = getDragHorizontalAngle(drag.x, drag.y);
    const dragValue = sin(dragAngle) === 0 ? Math.abs(drag.x / cos(dragAngle)) : Math.abs(drag.y / sin(dragAngle));
    const resize = dragValue * cos(-dragAngle);

    const resizedWidth = width - resize;
    const newX = xFirst + resize;
    const newData = {
      xFrom: resizedWidth < minWidth ? xFirst : newX < 0 ? 0 : newX,
      xTo: xTo,
      yFrom: yFirst,
      yTo: yTo
    };
    onResize(newData);
  }
  const resizeItemLeftTop = (data: DraggableData) => {
    const origin = getHandlePosition('leftTop');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const resize = { width: drag.x, height: drag.y }

    const resizedWidth = width - resize.width;
    const newX = xFirst + resize.width;
    const resizedHeight = height - resize.height;
    const newY = yFirst + resize.height;
    const newData = {
      xFrom: resizedWidth < minWidth ? xFirst : newX < 0 ? 0 : newX,
      xTo: xTo,
      yFrom: resizedHeight < minHeight ? yFirst : newY < 0 ? 0 : newY,
      yTo: yTo
    };
    onResize(newData);
  }

  return (
    <>
      <ResizeBoxSvg width={width * scaleValue} height={height * scaleValue} x={xFirst * scaleValue} y={yFirst * scaleValue}
        xmlns="http://www.w3.org/2000/svg">
        <Draggable position={getHandlePosition('top')} scale={scaleValue}
          onStart={() => dragStart('ns-resize')}
          onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemTop(data)}
          onStop={dragEnd}
          >
          <ResizeHandleRect width={width / 3 * scaleValue} height={4} fill={color} stroke={color} strokeWidth="1"
            cursor={dragging ? undefined : 'ns-resize'}
            translateValue={{x: width / 3 * scaleValue + 1, y: -1.5}} />
        </Draggable>
        <Draggable position={getHandlePosition('rightTop')} scale={scaleValue}
          onStart={() => dragStart('nesw-resize')}
          onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemRightTop(data)}
          onStop={dragEnd}
          >
          <ResizeHandleG fill={color} stroke={color} strokeWidth="1" cursor={dragging ? undefined : 'nesw-resize'}>
            <ResizeHandleRect width={cornerWidth} height={4}
              translateValue={{x: width * scaleValue - cornerWidth + 1.5, y: -1.5}} />
            <ResizeHandleRect width={4} height={cornerWidth}
              translateValue={{x: width * scaleValue - 2.5, y: -1.5}} />
          </ResizeHandleG>
        </Draggable>
        <Draggable position={getHandlePosition('right')} scale={scaleValue}
          onStart={() => dragStart('ew-resize')}
          onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemRight(data)}
          onStop={dragEnd}
          >
          <ResizeHandleRect width={4} height={height / 3 * scaleValue} fill={color} stroke={color} strokeWidth="1"
            cursor={dragging ? undefined : 'ew-resize'}
            translateValue={{x: width * scaleValue - 2.5, y: height / 3 * scaleValue + 1}} />
        </Draggable>
        <Draggable position={getHandlePosition('rightBottom')} scale={scaleValue}
          onStart={() => dragStart('nwse-resize')}
          onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemRightBottom(data)}
          onStop={dragEnd}
          >
          <ResizeHandleG fill={color} stroke={color} strokeWidth="1" cursor={dragging ? undefined : 'nwse-resize'}>
            <ResizeHandleRect width={cornerWidth} height={4}
              translateValue={{x: width * scaleValue - cornerWidth + 1.5, y: height * scaleValue - 2.5}} />
            <ResizeHandleRect width={4} height={cornerWidth}
              translateValue={{x: width * scaleValue - 2.5, y: height * scaleValue - cornerWidth + 1.5}} />
          </ResizeHandleG>
        </Draggable>
        <Draggable position={getHandlePosition('bottom')} scale={scaleValue}
          onStart={() => dragStart('ns-resize')}
          onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemBottom(data)}
          onStop={dragEnd}
          >
          <ResizeHandleRect width={width / 3 * scaleValue} height={4} fill={color} stroke={color} strokeWidth="1"
            cursor={dragging ? undefined : 'ns-resize'}
            translateValue={{x: width / 3 * scaleValue + 1, y: height * scaleValue - 2.5}} />
        </Draggable>
        <Draggable position={getHandlePosition('leftBottom')} scale={scaleValue}
          onStart={() => dragStart('nesw-resize')}
          onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemLeftBottom(data)}
          onStop={dragEnd}
          >
          <ResizeHandleG fill={color} stroke={color} strokeWidth="1" cursor={dragging ? undefined : 'nesw-resize'}>
            <ResizeHandleRect width={cornerWidth} height={4}
              translateValue={{x: -1.5, y: height * scaleValue - 2.5}} />
            <ResizeHandleRect width={4} height={cornerWidth}
              translateValue={{x: -1.5, y: height * scaleValue - cornerWidth + 1.5}} />
          </ResizeHandleG>
        </Draggable>
        <Draggable position={getHandlePosition('left')} scale={scaleValue}
          onStart={() => dragStart('ew-resize')}
          onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemLeft(data)}
          onStop={dragEnd}
          >
          <ResizeHandleRect width={4} height={height / 3 * scaleValue} fill={color} stroke={color} strokeWidth="1"
            cursor={dragging ? undefined : 'ew-resize'}
            translateValue={{x: -1.5, y: height / 3 * scaleValue + 1}} />
        </Draggable>
        <Draggable position={getHandlePosition('leftTop')} scale={scaleValue}
          onStart={() => dragStart('nwse-resize')}
          onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemLeftTop(data)}
          onStop={dragEnd}
          >
          <ResizeHandleG fill={color} stroke={color} strokeWidth="1" cursor={dragging ? undefined : 'nwse-resize'}>
            <ResizeHandleRect width={cornerWidth} height={4}
              translateValue={{x: -1.5, y: -1.5}} />
            <ResizeHandleRect width={4} height={cornerWidth}
              translateValue={{x: -1.5, y: -1.5}} />
          </ResizeHandleG>
        </Draggable>
      </ResizeBoxSvg>
    </>
  );
}

type CardResizeModalProps = {
  open: boolean;
  onClose: () => void;
  id: string;
  modalX?: number;
  modalY?: number;
  scale: number;
}
const CardResizeModal: React.FC<CardResizeModalProps> = ({open, onClose, id, modalX, modalY, scale}) => {
  const dispatch = useDispatch();
  const card = useOneCardDb(id);
  const xFrom = isMapCard(card) ? card.x : 0;
  const width = card?.currentWidth || card?.defaultWidth || initial.cardWidth;
  const xTo = xFrom + width;
  const yFrom = isMapCard(card) ? card.y : 0;
  const height = card?.currentHeight || card?.defaultHeight || initial.cardHeight;
  const yTo = yFrom + height;
  const [resizeData, setResizeData] = useState<ResizeDataType>({ xFrom, xTo, yFrom, yTo });
  const [position, setPosition] = useState({ x: modalX || 0, y: modalY || 0 });
  if (!isMapCard(card)) return <></>;
  const [minWidth, minHeight] = [card.defaultWidth, card.defaultHeight];

  const changeData = (data: ResizeDataType) => {
    setResizeData(pre => {
      const diffX = (data.xFrom - pre.xFrom) * scale;
      const diffY = (data.yFrom - pre.yFrom) * scale;
      setPosition({ x: position.x + diffX, y: position.y + diffY });
      return data;
    });
  }
  const exitModal = () => {
    const x = resizeData.xFrom;
    const y = resizeData.yFrom;
    const currentWidth = resizeData.xTo - resizeData.xFrom;
    const currentHeight = resizeData.yTo - resizeData.yFrom;
    dispatch(updateCard({...card, id, x, y, currentWidth, currentHeight}));
    onClose();
  }

  return (
    <Modal open={open} onClose={exitModal}>
      <ModalContent x={position.x} y={position.y}>
        <ScaleWrapper scale={scale}>
          <ResizeCardPad width={resizeData.xTo - resizeData.xFrom} height={resizeData.yTo - resizeData.yFrom}>
            { getCardComponent(card.kind, id, resizeData.xTo - resizeData.xFrom, resizeData.yTo - resizeData.yFrom) }
          </ResizeCardPad>
        </ScaleWrapper>
        <ResizeBox resizeData={resizeData} onResize={changeData} scaleValue={scale} minWidth={minWidth} minHeight={minHeight} />
      </ModalContent>
    </Modal>
  );
}

export default CardResizeModal;
